import type { FormEvent, ReactNode } from 'react'
import type { PaymentStatus, VisitInput } from '@/types/clinic'

import { useState } from 'react'
import { toast } from 'sonner'
import { Check, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClinic } from '@/lib/clinic/store'
import { addDays, addMonths, formatDate } from '@/lib/dates'
import { roundMoney } from '@/lib/format'
import { getInvoiceSummary } from '@/lib/clinic/selectors'
import { useMoney } from '@/hooks/use-clinic-helpers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import FormField from '@/components/app/form-field'
import SimpleSelect from '@/components/app/simple-select'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import { PAYMENT_STATUSES, PAYMENT_STATUS_META } from '@/constants/clinic'

export interface VisitDialogOptions {
    patientId: string
    visitId?: string
    appointmentId?: string
}

interface VisitDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    options: VisitDialogOptions
}

interface FormState {
    date: string
    complaint: string
    diagnosis: string
    treatmentIds: string[]
    toothNumbers: string
    notes: string
    prescriptionNotes: string
    nextVisitDate: string
    followUpNotes: string
    cost: string
    costTouched: boolean
    paymentStatus: PaymentStatus
}

const VisitDialog = ({ open, onOpenChange, options }: VisitDialogProps): ReactNode => {
    const { data, today, actions } = useClinic()
    const money = useMoney()
    const patient = data.patients.find(item => item.id === options.patientId)
    const existing = options.visitId ? data.visits.find(item => item.id === options.visitId) : undefined
    const appointment = options.appointmentId ? data.appointments.find(item => item.id === options.appointmentId) : undefined
    const linkedInvoice = existing ? data.invoices.find(item => item.visitId === existing.id) : undefined
    const linkedFollowUp = existing ? data.followUps.find(item => item.visitId === existing.id && item.status === 'pending') : undefined

    const priceOf = (ids: string[]) => roundMoney(ids.reduce((sum, id) => sum + (data.treatments.find(item => item.id === id)?.price ?? 0), 0))

    const [form, setForm] = useState<FormState>(() => {
        const treatmentIds = existing
            ? existing.treatments.map(ref => ref.treatmentId).filter((id): id is string => Boolean(id))
            : appointment?.treatmentId ? [appointment.treatmentId] : []
        return {
            date: existing?.date ?? appointment?.date ?? today,
            complaint: existing?.complaint ?? appointment?.notes ?? '',
            diagnosis: existing?.diagnosis ?? '',
            treatmentIds,
            toothNumbers: existing?.toothNumbers ?? '',
            notes: existing?.notes ?? '',
            prescriptionNotes: existing?.prescriptionNotes ?? '',
            nextVisitDate: existing?.nextVisitDate ?? '',
            followUpNotes: linkedFollowUp?.notes ?? '',
            cost: String(existing ? existing.cost : priceOf(treatmentIds)),
            costTouched: Boolean(existing),
            paymentStatus: existing?.paymentStatus ?? 'pending'
        }
    })
    const [submitted, setSubmitted] = useState(false)

    const update = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm(current => ({ ...current, [key]: value }))

    const selectableTreatments = data.treatments.filter(item => item.active || form.treatmentIds.includes(item.id))
    // Treatments recorded on an older visit that no longer exist in the catalog.
    const removedTreatments = existing ? existing.treatments.filter(ref => !ref.treatmentId || !data.treatments.some(item => item.id === ref.treatmentId)) : []

    const toggleTreatment = (id: string) => {
        setForm(current => {
            const treatmentIds = current.treatmentIds.includes(id)
                ? current.treatmentIds.filter(item => item !== id)
                : [...current.treatmentIds, id]
            return { ...current, treatmentIds, cost: current.costTouched ? current.cost : String(priceOf(treatmentIds)) }
        })
    }

    const cost = Number(form.cost)
    const errors = {
        date: !form.date ? 'Choose the visit date.' : '',
        treatments: form.treatmentIds.length === 0 && removedTreatments.length === 0 ? 'Select at least one treatment.' : '',
        cost: form.cost === '' || Number.isNaN(cost) || cost < 0 ? 'Enter a valid amount.' : '',
        nextVisitDate: form.nextVisitDate && form.date && form.nextVisitDate <= form.date ? 'The next visit must be after this visit.' : ''
    }
    const hasErrors = Object.values(errors).some(Boolean)

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        setSubmitted(true)
        if (hasErrors || !patient) {
            toast.error('Please complete the highlighted fields')
            return
        }

        const treatments = [
            ...removedTreatments,
            ...form.treatmentIds.map(id => {
                const treatment = data.treatments.find(item => item.id === id)
                return { treatmentId: id, name: treatment?.name ?? 'Treatment' }
            })
        ]

        const payload: VisitInput = {
            patientId: patient.id,
            appointmentId: existing?.appointmentId ?? appointment?.id ?? null,
            date: form.date,
            complaint: form.complaint.trim(),
            diagnosis: form.diagnosis.trim(),
            treatments,
            toothNumbers: form.toothNumbers.trim(),
            notes: form.notes.trim(),
            prescriptionNotes: form.prescriptionNotes.trim(),
            nextVisitDate: form.nextVisitDate,
            cost: roundMoney(cost),
            paymentStatus: form.paymentStatus,
            followUpNotes: form.followUpNotes.trim()
        }

        if (existing) {
            actions.updateVisit(existing.id, payload)
            toast.success('Visit updated', { description: `${patient.name}, ${formatDate(form.date)}` })
        }
        else {
            actions.addVisit(payload)
            toast.success('Visit recorded', {
                description: form.nextVisitDate ? `Follow-up added for ${formatDate(form.nextVisitDate)}` : `${patient.name}, ${formatDate(form.date)}`
            })
        }
        onOpenChange(false)
    }

    const quickNext = [
        { label: '1 week', value: addDays(form.date || today, 7) },
        { label: '2 weeks', value: addDays(form.date || today, 14) },
        { label: '1 month', value: addMonths(form.date || today, 1) },
        { label: '6 months', value: addMonths(form.date || today, 6) }
    ]

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl'>
                <form onSubmit={handleSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>{existing ? 'Edit visit' : 'Add visit'}</DialogTitle>
                        <DialogDescription>
                            {patient ? `${patient.name} (${patient.code}). Each visit is saved as a separate record in the history.` : 'Record the details of this visit.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <FormField label='Visit date' htmlFor='visit-date' required error={submitted ? errors.date : ''}>
                            <Input
                                id='visit-date'
                                type='date'
                                value={form.date}
                                max={today}
                                onChange={event => update('date', event.target.value)}
                                aria-invalid={submitted && Boolean(errors.date)}
                            />
                        </FormField>

                        <FormField label='Tooth number' htmlFor='visit-tooth' hint='Optional, for example 36 or 24, 25'>
                            <Input
                                id='visit-tooth'
                                value={form.toothNumbers}
                                onChange={event => update('toothNumbers', event.target.value)}
                                placeholder='36'
                            />
                        </FormField>

                        <FormField label='Dental problem / complaint' htmlFor='visit-complaint' className='sm:col-span-2'>
                            <Input
                                id='visit-complaint'
                                value={form.complaint}
                                onChange={event => update('complaint', event.target.value)}
                                placeholder='Pain in lower right back tooth for three days'
                            />
                        </FormField>

                        <FormField label='Diagnosis' htmlFor='visit-diagnosis' className='sm:col-span-2'>
                            <Input
                                id='visit-diagnosis'
                                value={form.diagnosis}
                                onChange={event => update('diagnosis', event.target.value)}
                                placeholder='Irreversible pulpitis in 46'
                            />
                        </FormField>

                        <div className='grid gap-1.5 sm:col-span-2'>
                            <p id='visit-treatments-label' className='text-[13px] font-medium text-foreground/90'>
                                Treatment performed <span className='text-destructive' aria-hidden='true'>*</span>
                            </p>
                            <div role='group' aria-labelledby='visit-treatments-label' className='flex flex-wrap gap-2'>
                                {selectableTreatments.map(treatment => {
                                    const selected = form.treatmentIds.includes(treatment.id)
                                    return (
                                        <button
                                            key={treatment.id}
                                            type='button'
                                            aria-pressed={selected}
                                            onClick={() => toggleTreatment(treatment.id)}
                                            className={cn(
                                                'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                                selected
                                                    ? 'border-primary bg-primary/10 text-primary'
                                                    : 'border-border bg-card text-foreground hover:border-primary/40 hover:bg-accent'
                                            )}
                                        >
                                            {selected && <Check className='size-3.5' aria-hidden='true' />}
                                            {treatment.name}
                                        </button>
                                    )
                                })}
                                {removedTreatments.map(ref => (
                                    <span key={ref.name} className='inline-flex h-8 items-center rounded-full border border-dashed px-3 text-xs text-muted-foreground'>
                                        {ref.name} (removed from catalog)
                                    </span>
                                ))}
                            </div>
                            {submitted && errors.treatments && <p role='alert' className='text-xs font-medium text-destructive'>{errors.treatments}</p>}
                        </div>

                        <FormField label='Doctor notes' htmlFor='visit-notes' className='sm:col-span-2'>
                            <Textarea
                                id='visit-notes'
                                value={form.notes}
                                onChange={event => update('notes', event.target.value)}
                                placeholder='Procedure details, observations and advice'
                                rows={3}
                            />
                        </FormField>

                        <FormField label='Prescription' htmlFor='visit-prescription' hint='Short summary. Create a printable prescription from the patient profile.' className='sm:col-span-2'>
                            <Textarea
                                id='visit-prescription'
                                value={form.prescriptionNotes}
                                onChange={event => update('prescriptionNotes', event.target.value)}
                                placeholder='Amoxicillin 500 mg (5 days), Ibuprofen 400 mg (3 days)'
                                rows={2}
                            />
                        </FormField>

                        <FormField label='Next visit date' htmlFor='visit-next' error={submitted ? errors.nextVisitDate : ''} hint='Adds the patient to follow-ups'>
                            <Input
                                id='visit-next'
                                type='date'
                                value={form.nextVisitDate}
                                min={form.date || undefined}
                                onChange={event => update('nextVisitDate', event.target.value)}
                                aria-invalid={submitted && Boolean(errors.nextVisitDate)}
                            />
                            <div className='flex flex-wrap gap-1.5'>
                                {quickNext.map(option => (
                                    <button
                                        key={option.label}
                                        type='button'
                                        onClick={() => update('nextVisitDate', option.value)}
                                        className='h-7 rounded-md border bg-card px-2 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                    >
                                        +{option.label}
                                    </button>
                                ))}
                                {form.nextVisitDate && (
                                    <button
                                        type='button'
                                        onClick={() => update('nextVisitDate', '')}
                                        className='h-7 rounded-md px-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline'
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </FormField>

                        <FormField label='Follow-up note' htmlFor='visit-follow-up-note'>
                            <Input
                                id='visit-follow-up-note'
                                value={form.followUpNotes}
                                onChange={event => update('followUpNotes', event.target.value)}
                                placeholder='Crown preparation for 46'
                                disabled={!form.nextVisitDate}
                            />
                        </FormField>

                        <FormField
                            label='Treatment cost'
                            htmlFor='visit-cost'
                            required
                            error={submitted ? errors.cost : ''}
                            hint={form.costTouched && Number(form.cost) !== priceOf(form.treatmentIds)
                                ? <button type='button' className='underline underline-offset-2 hover:text-foreground' onClick={() => setForm(current => ({ ...current, cost: String(priceOf(current.treatmentIds)), costTouched: false }))}>Use catalog price ({money(priceOf(form.treatmentIds))})</button>
                                : 'Filled from treatment prices, you can edit it'}
                        >
                            <Input
                                id='visit-cost'
                                type='number'
                                inputMode='decimal'
                                min={0}
                                step='1'
                                value={form.cost}
                                onChange={event => setForm(current => ({ ...current, cost: event.target.value, costTouched: true }))}
                                aria-invalid={submitted && Boolean(errors.cost)}
                            />
                        </FormField>

                        <FormField label='Payment status' htmlFor='visit-payment'>
                            {linkedInvoice
                                ? (
                                    <p className='flex h-9 items-center gap-2 rounded-lg border border-dashed px-3 text-sm text-muted-foreground'>
                                        <Info className='size-4 shrink-0' aria-hidden='true' />
                                        Follows bill {linkedInvoice.number} ({PAYMENT_STATUS_META[getInvoiceSummary(linkedInvoice).status].label})
                                    </p>
                                )
                                : (
                                    <SimpleSelect
                                        id='visit-payment'
                                        value={form.paymentStatus}
                                        onValueChange={value => update('paymentStatus', value as PaymentStatus)}
                                        options={PAYMENT_STATUSES.map(status => ({ value: status, label: PAYMENT_STATUS_META[status].label }))}
                                    />
                                )}
                        </FormField>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Cancel
                        </DialogClose>
                        <Button type='submit'>
                            {existing ? 'Save visit' : 'Add visit'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default VisitDialog
